import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles } from '../../ducks/organizations.duck';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { orgListingsAPI, assignmentsAPI } from '../../api';
import config from '../../config';
import { PAGE_TYPE_QUERY } from '../../util/listingType';

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/OrgManageShowsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/OrgManageShowsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/OrgManageShowsPage/FETCH_LISTINGS_ERROR';

export const DISCARD_DRAFT_REQUEST = 'app/OrgManageShowsPage/DISCARD_DRAFT_REQUEST';
export const DISCARD_DRAFT_SUCCESS = 'app/OrgManageShowsPage/DISCARD_DRAFT_SUCCESS';
export const DISCARD_DRAFT_ERROR = 'app/OrgManageShowsPage/DISCARD_DRAFT_ERROR';

export const SET_ACTIVE_TAB = 'app/OrgManageShowsPage/SET_ACTIVE_TAB';

export const SET_ORG_ID = 'app/OrgManageShowsPage/SET_ORG_ID';

export const FETCH_ASSIGNMENTS_REQUEST = 'app/OrgManageShowsPage/FETCH_ASSIGNMENTS_REQUEST';
export const FETCH_ASSIGNMENTS_SUCCESS = 'app/OrgManageShowsPage/FETCH_ASSIGNMENTS_SUCCESS';
export const FETCH_ASSIGNMENTS_ERROR = 'app/OrgManageShowsPage/FETCH_ASSIGNMENTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  listingIds: [],
  queryInProgress: false,
  queryLoaded: false,
  queryListingsError: null,
  discardingDraft: null,
  discardingDraftError: null,
  activeTab: 'published',
  // Org's assignments — used to render assigned Live Rooms nested under
  // each Show row. Read-only on this page (full mgmt lives in
  // OrgSettingsAssignmentsPage).
  assignments: [],
  assignmentsLoading: false,
  assignmentsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_ORG_ID:
      // Switching orgs (same mounted component, different :orgId in the URL)
      // must drop the previous org's data so it can't flash under the new
      // org while its fetch is in flight. Same orgId → keep state so a
      // revisit/refetch stays stale-while-revalidate.
      return payload === state.orgId ? state : { ...initialState, orgId: payload };
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryInProgress: true,
        queryListingsError: null,
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        listingIds: payload.listingIds,
        queryInProgress: false,
        queryLoaded: true,
      };
    case FETCH_LISTINGS_ERROR:
      return {
        ...state,
        queryInProgress: false,
        queryListingsError: payload,
      };

    case DISCARD_DRAFT_REQUEST:
      return {
        ...state,
        discardingDraft: payload.listingId,
        discardingDraftError: null,
      };
    case DISCARD_DRAFT_SUCCESS:
      return {
        ...state,
        discardingDraft: null,
        // Remove the discarded listing from the list
        listingIds: state.listingIds.filter(id => id.uuid !== payload.listingId),
      };
    case DISCARD_DRAFT_ERROR:
      return {
        ...state,
        discardingDraft: null,
        discardingDraftError: payload,
      };

    case SET_ACTIVE_TAB:
      return { ...state, activeTab: payload };

    case FETCH_ASSIGNMENTS_REQUEST:
      return { ...state, assignmentsLoading: true, assignmentsError: null };
    case FETCH_ASSIGNMENTS_SUCCESS:
      return {
        ...state,
        assignmentsLoading: false,
        assignments: payload || [],
      };
    case FETCH_ASSIGNMENTS_ERROR:
      return { ...state, assignmentsLoading: false, assignmentsError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const queryListingsRequest = () => ({
  type: FETCH_LISTINGS_REQUEST,
});

export const queryListingsSuccess = listingIds => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { listingIds },
});

export const queryListingsError = error => ({
  type: FETCH_LISTINGS_ERROR,
  payload: error,
});

export const discardDraftRequest = listingId => ({
  type: DISCARD_DRAFT_REQUEST,
  payload: { listingId },
});

export const discardDraftSuccess = listingId => ({
  type: DISCARD_DRAFT_SUCCESS,
  payload: { listingId },
});

export const discardDraftError = error => ({
  type: DISCARD_DRAFT_ERROR,
  payload: error,
});

export const setActiveTab = tab => ({
  type: SET_ACTIVE_TAB,
  payload: tab,
});

export const setOrgId = orgId => ({
  type: SET_ORG_ID,
  payload: orgId,
});

// ================ Thunks ================ //

export const queryListings = orgId => async dispatch => {
  dispatch(queryListingsRequest());

  try {
    // Org-scoped read: route through the server's Integration SDK proxy
    // so non-owner team members (admin/manager/member) also see the org's
    // listings. The server forces pub_organizationId=orgId; everything
    // else mirrors what sdk.ownListings.query would have accepted.
    const response = await orgListingsAPI.query(orgId, {
      pub_type: PAGE_TYPE_QUERY,
      states: 'draft,pendingApproval,published,closed',
      include: 'images,author',
      'fields.image': 'variants.square-small,variants.square-small2x',
      'limit.images': 1,
    });

    const listings = response?.data || [];
    const listingIds = listings.map(l => l.id);

    // marketplaceData duck expects { data: { data, included } }; transit
    // deserialization already rehydrated UUIDs/Money/etc on the wire, so
    // we can hand the body straight in.
    dispatch(addMarketplaceEntities({ data: response }));
    dispatch(queryListingsSuccess(listingIds));

    return listings;
  } catch (e) {
    dispatch(queryListingsError(storableError(e)));
    throw e;
  }
};

export const discardDraft = listingId => async (dispatch, getState, sdk) => {
  dispatch(discardDraftRequest(listingId));

  try {
    const response = await sdk.ownListings.discardDraft({ id: listingId });
    dispatch(discardDraftSuccess(listingId));
    return response;
  } catch (e) {
    dispatch(discardDraftError(storableError(e)));
    throw e;
  }
};

// Archive (close) a published show. Mirrors discardDraft's owner-scoped
// sdk.ownListings usage; feeds the updated listing back through
// addMarketplaceEntities so the row reflects its new 'closed' state.
export const closeListing = listingId => async (dispatch, getState, sdk) => {
  try {
    const response = await sdk.ownListings.close(
      { id: listingId },
      {
        expand: true,
        include: ['images', 'author'],
        'fields.image': 'variants.square-small,variants.square-small2x',
        'limit.images': 1,
      }
    );
    dispatch(addMarketplaceEntities(response));
    return response;
  } catch (e) {
    dispatch(queryListingsError(storableError(e)));
    throw e;
  }
};

// Fetch the org's assignments so ShowItem rows can render their
// assigned Live Rooms nested below. Read-only here; full management
// lives on OrgSettingsAssignmentsPage.
export const fetchOrgAssignments = orgId => async dispatch => {
  dispatch({ type: FETCH_ASSIGNMENTS_REQUEST });
  try {
    const res = await assignmentsAPI.listForOrg(orgId);
    const body = res?.data ?? res ?? {};
    const data = body?.data ?? body;
    const assignments = Array.isArray(data?.assignments) ? data.assignments : [];
    dispatch({ type: FETCH_ASSIGNMENTS_SUCCESS, payload: assignments });
    return assignments;
  } catch (e) {
    dispatch({ type: FETCH_ASSIGNMENTS_ERROR, payload: storableError(e) });
    return [];
  }
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  const { orgId } = params;

  // Reset the slice when the org actually changes so the previous org's
  // shows can't flash under the new org during its fetch.
  dispatch(setOrgId(orgId));

  // Ensure user is logged in
  await dispatch(fetchCurrentUser());

  // Fetch organization profiles (needed for DashboardWrapper)
  await dispatch(fetchOrganizationProfiles());

  // Listings (load-bearing) + assignments (presentational nesting).
  // Don't block the page render on the assignments fetch.
  dispatch(fetchOrgAssignments(orgId)).catch(() => null);
  return dispatch(queryListings(orgId));
};
